'use strict';

import {BaseElement} from "../../../../../interfaces/BaseElement";
import {
	findSelectionAndCrmData,
	getFieldsFromAPI,
	isRoofExists,
} from "../../../../../../functions";
import {changeLangToCurrent, getCurrentLang} from "../../../../../components/langSwitcher/langSwitcher";
import {
	addFavoritesBlock,
	addFloor,
} from "../../../../../cards/cardsFunctions";
import {Price} from "../../../../../cards/Price";
import {CardTypes} from "../../../../../../common";
import {openOptionsCard} from "../../../../../cards/optionsCards";
import {keyPlanHandlerThrottling} from "../../../../../cards/unitCards/unitCardFunctions";
import {dataArrayOfFlat, facingDictionary} from "../../../../../cards/cardsConfig";
import {Buildings, projectMode, settingsData} from "../../../../../../settings";
import {BuildingSwitcher} from "../BuildingSwitcher";

export class ApartmentCard {
	#rootElement;
	#floorWrapper;
	#infoElement;
	#data;
	#headerElement;
	#propertyRootElement;

	constructor(apartmentData) {
		this.#data = apartmentData;
		this.#rootElement = new BaseElement()
			.addClass('results-viewer__apartment-card')
			.addAttribute('data-engineid', apartmentData.engineId)
			.addAttribute('data-building',apartmentData.buildingEngineId);

		this.#info();
		this.#horizontalSeparator();
		this.#additionalButtons();
	}

	get rootElement() {
		return this.#rootElement;
	}

	#info() {
		this.#infoElement = new BaseElement()
			.addClass('apartment-card__info')
			.appendTo(this.#rootElement);

		this.#basicInfo();
		this.#infoPoints();
	}

	#additionalButtons() {
		const wrapper = new BaseElement()
			.addClass('apartment-card__additional-buttons')
			.appendTo(this.#rootElement);

		const hasKeyPlan =
			!projectMode.LiteMode.modeIs('empty') &&
			!projectMode.LiteMode.modeIs('picture') &&
			Boolean(settingsData?.commonAssets?.[Buildings.getActiveBuilding().name]?.floorAmenities.filter(group =>  {
				parseInt(group.from) >= parseInt(this.#data.floor) && parseInt(group.to) <= parseInt(this.#data.floor)
			}));
		const isHideKeyPlan = projectMode.LiteMode.modeIs('empty') || projectMode.LiteMode.modeIs('picture') || (config.features?.hideKeyPlanButton || !isRoofExists() || (config.features?.plainKeyPlan && !hasKeyPlan));
		new BaseElement('button')
			.addClass('key-plan-button', isHideKeyPlan ? 'hidden' : null, 'language-string')
			.innerHtml(changeLangToCurrent('Key plan'))
			.addAttribute('data-dictionary', 'Key plan')
			.event('click', keyPlanHandlerThrottling)
			.appendTo(wrapper);

		new BaseElement('button')
			.addClass('explore-button', 'language-string')
			.innerHtml(changeLangToCurrent('Explore'))
			.addAttribute('data-dictionary', 'Explore')
			.event('click', (event) => {
				const apartmentCard = event.target.closest('.results-viewer__apartment-card');
				if (!apartmentCard)
					return;

				const apartmentEngineId = apartmentCard.getAttribute('data-engineid');
				if (!apartmentEngineId)
					return;

				const {crmDataOfFlat, selectedMesh} = findSelectionAndCrmData(apartmentEngineId);
				settingsData.lastClickedFlat = selectedMesh;
				openOptionsCard({object: crmDataOfFlat});
			}).appendTo(wrapper);

	}

	#basicInfo() {
		const propertyNumber = (parent) => {
			const propertyWrapper = new BaseElement()
				.addClass('apartment-card__property-wrapper')
				.appendTo(parent);

			new BaseElement()
				.addClass('apartment-card__property-title', 'language-string')
				.addAttribute('data-dictionary', this.#data.propType)
				.innerHtml("#")
				.appendTo(propertyWrapper);

			new BaseElement()
				.addClass('apartment-card__property-number')
				.innerHtml(this.#data.propNum)
				.appendTo(propertyWrapper);
		};

		const amountOfBedrooms = (parent) => {
			const cardInfo = dataArrayOfFlat(this.#data);
			const currentPointData = cardInfo.filter(data => data.name === 'bedrooms')[0];
			const pointWrapper = new BaseElement()
				.addClass('apartment-card__info-point')
				.appendTo(parent);
			new BaseElement('span')
				.addClass('info-point__title', 'language-string')
				.addAttribute('data-dictionary', currentPointData.name)
				.innerHtml(changeLangToCurrent(currentPointData.name))
				.appendTo(pointWrapper);

			new BaseElement('span')
				.addClass('info-point__value')
				.innerHtml(currentPointData.data)
				.appendTo(pointWrapper);

			pointWrapper.addStyle('flex-direction', 'row-reverse');
		}

		const rootElement = new BaseElement()
			.addClass('apartment-card__basic-info')
			.appendTo(this.#infoElement);

		this.#headerElement = new BaseElement()
			.addClass('apartment-card__header')
			.appendTo(rootElement);

		addFavoritesBlock(this.#data, this.#headerElement.DOMElement, CardTypes.MobileCard);

		this.#propertyRootElement = new BaseElement()
			.addClass('apartment-card__property-root')
			.appendTo(this.#headerElement);

		propertyNumber(this.#propertyRootElement.DOMElement);
		propertyNumber(rootElement.DOMElement);
		amountOfBedrooms(this.#propertyRootElement.DOMElement);

		const priceRootElement = new BaseElement()
			.addClass('apartment-card__price')
			.appendTo(rootElement);

		new Price()
			.addBlock(priceRootElement.DOMElement)
			.setPrice(this.#data);

		const labelsWrapper = new BaseElement('div')
			.addClass('apartment-card__labels-wrapper')
			.appendTo(this.#rootElement);

		const price2title = this.#rootElement.DOMElement.querySelector('.price2Title');
		if (price2title) {
			price2title.classList.add('card__label')
			labelsWrapper.appendElement(price2title);
		}

		if (this.#data.selectedProperty) {
			new BaseElement('span')
				.addClass('language-string', 'card__label', 'selected-property')
				.addAttribute('data-dictionary','selectedProperty')
				.innerHtml(changeLangToCurrent('selectedProperty'))
				.appendTo(labelsWrapper);

		} 

		this.#floorWrapper = new BaseElement()
			.addClass('apartment-card__floor-wrapper')
			.appendTo(rootElement);

		let isSeparatorNeeded = true;

		if (!Buildings.isOneFloorBuilding(this.#data.buildingEngineId)) {
			addFloor(this.#data, this.#floorWrapper.DOMElement, 'mobile');
		} else {
			isSeparatorNeeded = false;
		}

		if (Buildings.getBuildingsInfoLength() > 1) {
			const buildingNames = BuildingSwitcher.listOfBuildings.find(item => item.name === this.#data.buildingEngineId)?.towerNames;
			if (!buildingNames)
				return;
			let currentLanguageName = buildingNames.find(item => item.lang === getCurrentLang())?.value;
			if (!currentLanguageName)
				currentLanguageName = buildingNames.find(item => item.lang === 'en')?.value;

			const buildingNameElement = new BaseElement('span')
				.addClass('apartment-card__building-name', 'language-string')
				.innerHtml(currentLanguageName)
				.appendTo(this.#floorWrapper);

			buildingNames.forEach(languageItem => {
				buildingNameElement.addAttribute(`data-lang-${languageItem.lang}`, languageItem.value);
			});
		} else {
			isSeparatorNeeded = false;
		}

		if (isSeparatorNeeded) {
			new BaseElement()
				.addClass('apartment-card__dot-separator')
				.appendTo(this.#floorWrapper);
		}
	}

	#horizontalSeparator(parent) {
		new BaseElement()
			.addClass('apartment-card__horizontal-separator')
			.appendTo(parent || this.#rootElement);
	}

	#infoPoints() {
		const rootElement = new BaseElement()
			.addClass('apartment-card__info-points')
			.appendTo(this.#infoElement);

		const cardInfo = dataArrayOfFlat(this.#data);
		getFieldsFromAPI.smallCard().forEach((infoPoint) => {
			const currentPointData = cardInfo.filter(data => data.name === infoPoint)[0];
			if (currentPointData.data === 0 || currentPointData.data.length === 0 || infoPoint === 'bedrooms')
				return;

			const pointWrapper = new BaseElement()
				.addClass('apartment-card__info-point')
				.appendTo(rootElement.DOMElement);

			let valueToShow = currentPointData.data;
			if (infoPoint === 'parkings' && Array.isArray(currentPointData.data))
				valueToShow = currentPointData.data.length;
			if (infoPoint === 'storages' && Array.isArray(currentPointData.data)) {
				const valueCopy = [ ...currentPointData.data ];
				valueToShow = 0;
				valueCopy.forEach(item => {
					valueToShow += item.size;
				});
			}

			switch(infoPoint) {
				case 'accessible':
				case 'treatmentRoom':
				case 'tenantPrice':
					new BaseElement('span')
						.addClass('info-point__title', 'language-string')
						.addAttribute('data-dictionary', currentPointData.name)
						.innerHtml(changeLangToCurrent(currentPointData.name))
						.appendTo(pointWrapper.DOMElement);

					new BaseElement('span')
						.addClass('info-point__value', 'icon-check-mark')
						.appendTo(pointWrapper.DOMElement);
					break;
				case 'exposure':
					if (valueToShow.length === 0)
						return;

					const wrapper = new BaseElement();
					valueToShow.forEach(element => {
						for (let key in facingDictionary()) {
							if (element === key) {
								new BaseElement('span')
									.addClass('language-string')
									.addAttribute('data-dictionary',facingDictionary()[key].en + 'Short')
									.innerHtml(changeLangToCurrent(facingDictionary()[key].en + 'Short'))
									.appendTo(wrapper.DOMElement);
							}
						}
					});

					valueToShow = wrapper.DOMElement.innerHTML;

					new BaseElement('span')
						.addClass('info-point__value')
						.innerHtml(valueToShow)
						.appendTo(pointWrapper.DOMElement);
					break;
				case 'apt. type':
					new BaseElement('span')
						.addClass('info-point__title', 'language-string')
						.addAttribute('data-dictionary', currentPointData.name)
						.innerHtml(changeLangToCurrent(currentPointData.name))
						.appendTo(pointWrapper.DOMElement);

					const value = new BaseElement('span')
						.addClass('info-point__value', 'info-point__value--with-column')
						.innerHtml(`${valueToShow}`)
						.appendTo(pointWrapper.DOMElement);

					new BaseElement('span')
						.addClass('info-point__value-column')
						.innerHtml(':')
						.appendTo(value.DOMElement);

					pointWrapper.addStyle('gap', 0);
					break;
				case 'area':
				case 'balcony size':
				case 'roof balcony size':
				case 'covered balcony size':
				case 'basement size':
				case 'garden size':
				case 'storages':
					if (infoPoint !== 'area') {
						new BaseElement('span')
							.addClass('info-point__name', 'language-string')
							.innerHtml(changeLangToCurrent(infoPoint))
							.addAttribute('data-dictionary', infoPoint)
							.appendTo(pointWrapper.DOMElement);
					}

					new BaseElement('span')
						.addClass('info-point__value')
						.innerHtml(`${valueToShow}`)
						.appendTo(pointWrapper.DOMElement);

					const measurement = config.localization.area ? config.localization.area : "m2";
					const measurementTag = new BaseElement('span')
						.addClass('language-string', 'info-point__measurement', 'meters')
						.addAttribute('data-dictionary', measurement)
						.addStyle('margin-inline-end', '5px')
						.appendTo(pointWrapper.DOMElement)
						.DOMElement;
					measurementTag.innerHTML += changeLangToCurrent(measurement);
					break;
				default:
					new BaseElement('span')
						.addClass('info-point__title', 'language-string')
						.addAttribute('data-dictionary', currentPointData.name)
						.innerHtml(changeLangToCurrent(currentPointData.name))
						.appendTo(pointWrapper.DOMElement);

					new BaseElement('span')
						.addClass('info-point__value')
						.innerHtml(valueToShow)
						.appendTo(pointWrapper.DOMElement);
					break;
			}
		})
	}
}
